import { profileTokens } from './profileTokens';
import { session } from './session';

// The single owner of the rule "the DEFAULT business profile is served by the SESSION token,
// every other profile by its own per-BP app token" (see docs/auth-token-businessprofile-sync.md).
//
// Why one module: the rule used to be re-derived in three places (stores/auth.js snapshots,
// tokenService's private predicate, ad-hoc guards) — any copy drifting or failing open re-created
// a second owner of the session's refresh chain, which trips backend refresh-token reuse-detection
// and revokes the whole session (the WP-981 spontaneous-logout bug). Callers must route every
// per-BP token read/mint/refresh through this resolver and never call profileTokens directly
// with an id that might be the default profile.
//
// Import direction: session.js already imports profileTokens, so this module imports BOTH and
// neither of them knows about it (no cycle). Keep it dependency-light — it is on the hot path
// of every business-API request.

// The id of the profile served by the session token, as a string, or null (pre-profile session).
// Deliberately claim-only — NO fallback to /users/me.businessProfileId: that field's contract is
// unconfirmed (it may reflect the LAST-USED profile, which after a mid-session switch differs
// from the BP the session token is scoped to), and misclassifying a non-default profile as
// default would send the session token for another profile's business requests — wrong data,
// worse than a loud failure. If the claim ever goes missing while a session exists, that's a
// broken token contract — surface it loudly (prod console too) instead of guessing.
function sessionBpId() {
  const fromToken = session.getSessionBpId();
  if (fromToken == null && session.hasSession() && session.user.value?.businessProfileId != null) {
    console.error(
      '[auth] session token carries no businessProfileId claim while /users/me reports a profile — the token contract drifted; default-profile routing is disabled until it is fixed.',
    );
  }
  return fromToken;
}

// Whether this id is the default profile (served by the session token, no per-BP entry).
export function isDefaultBp(bpId) {
  if (bpId == null) return false;
  const defaultId = sessionBpId();
  return defaultId != null && String(bpId) === defaultId;
}

// Synchronous snapshots — safe for computeds/CASL (never trigger network I/O).
export function tokenFor(bpId) {
  return isDefaultBp(bpId) ? session.getSessionAccessToken() : profileTokens.getFor(bpId);
}

export function scopesFor(bpId) {
  return isDefaultBp(bpId) ? session.getSessionScopes() : profileTokens.scopesFor(bpId);
}

export function isExpired(bpId) {
  return isDefaultBp(bpId) ? session.isSessionExpired() : profileTokens.isExpired(bpId);
}

// Ensure a valid app token exists for a NON-default BP: refresh it when we hold a refresh token,
// otherwise MINT a fresh one by switching the backend session to that profile. Minting (not only
// refreshing) keeps callers self-sufficient — a per-BP request that races ahead of the auth-store
// watcher's initial mint, or fires after a cross-tab clear, would otherwise throw.
// `force` re-mints even when a cached token still looks time-valid (the 401 path, where the
// backend rejected an otherwise-unexpired token). A background mint does NOT record last-used.
async function ensureAppToken(bpId, { force = false } = {}) {
  if (!force && !profileTokens.isExpired(bpId)) return;
  if (profileTokens.hasRefreshToken(bpId)) {
    try {
      await profileTokens.refresh(bpId, { force });
      return;
    } catch (e) {
      // Stale per-BP refresh token (e.g. left in localStorage from before a backend auth
      // deploy) — recoverable while the session is alive: fall through to mint a fresh
      // app token via /auth/switch-profile instead of failing the request.
      console.warn(
        `[auth] per-BP token refresh failed for BP ${bpId}, re-minting via switch-profile:`,
        e?.message,
      );
    }
  }
  if (!session.hasSession()) {
    // No session to mint a per-BP token from. Happens during the session-expiry / logout → login
    // transition while a per-BP request is still in flight. The forced-logout / redirect path
    // handles the real UX; the code tag lets the alerts store silence the toast.
    const error = new Error(`No app token and no session for BP ${bpId}`);
    error.code = 'ERR_NO_SESSION';
    throw error;
  }
  await profileTokens.fetchInitial(bpId, { force });
}

// A pre-profile session (fresh registration/confirm — no businessProfileId claim yet) has not
// adopted any profile. The FIRST profile that becomes current is the user's only/default profile
// and MUST be adopted into the SESSION token, never minted as a wp.auth.bp.<id> app token: that
// would create a second owner of the session's refresh chain, trip backend reuse-detection, and
// force-log the user out (the WP-981 shared-sid relapse). Re-scope the session to it (single
// owner) instead of minting, then let the caller serve the session token.
//
// Deadlock-safe: this runs only from stacks that hold NO session lock (axios interceptors and
// store watchers). claimDefaultProfile → switchToDefaultProfile acquires AUTH_REFRESH_LOCK_SESSION
// uncontended from here and fires only /auth/switch-profile (which never re-enters this resolver).
//
// Returns true iff the session is now scoped to THIS id (caller should serve the session token);
// false when the session was already scoped or landed on a different id (normal per-BP path).
async function reScopePreProfileDefault(id) {
  if (!session.hasSession() || session.getSessionBpId() != null) return false;
  await session.claimDefaultProfile(id);
  return isDefaultBp(id);
}

// The token for a business-API request scoped to this BP, refreshed/minted if needed.
export async function getValidTokenFor(bpId) {
  const id = String(bpId);
  if (isDefaultBp(id)) return session.getValidSessionToken();
  if (await reScopePreProfileDefault(id)) return session.getValidSessionToken();
  await ensureAppToken(id);
  return profileTokens.getFor(id);
}

// Non-forced refresh (e.g. the silent post-500 heal): rotate only when time-expired.
export async function refresh(bpId) {
  const id = String(bpId);
  if (isDefaultBp(id)) return session.refreshSession();
  return profileTokens.refresh(id);
}

// Force-rotate after a 401: the backend rejected a token that may not be time-expired.
export async function forceRefresh(bpId) {
  const id = String(bpId);
  if (isDefaultBp(id)) return session.refreshSession(true);
  if (await reScopePreProfileDefault(id)) return session.getValidSessionToken();
  return ensureAppToken(id, { force: true });
}

// Lazy mint for a BP that has no valid token yet (auth-store watcher / account sync).
export async function fetchInitial(bpId) {
  const id = String(bpId);
  if (isDefaultBp(id)) return session.getValidSessionToken();
  if (await reScopePreProfileDefault(id)) return session.getValidSessionToken();
  return profileTokens.fetchInitial(id);
}

// Explicit user-initiated switch: always hits /auth/switch-profile so the backend re-issues the
// scoped token AND records the selection as the user's last-used profile. Switching back to the
// default profile rotates the session's OWN chain into wp.auth.session (single owner of that
// sid); other profiles get their own per-BP token.
export async function switchTo(bpId) {
  const id = String(bpId);
  if (isDefaultBp(id)) return session.switchToDefaultProfile(id);
  return profileTokens.switchTo(id);
}

export const tokenResolver = {
  isDefaultBp,
  tokenFor,
  scopesFor,
  isExpired,
  getValidTokenFor,
  refresh,
  forceRefresh,
  fetchInitial,
  switchTo,
};
